import { formatDate } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnInit, Optional, Self } from '@angular/core';
import { AbstractControl, FormControl, FormControlStatus, FormGroup, NgControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { MatFormFieldControl } from '@angular/material/form-field';
import { takeUntil } from 'rxjs';
import { FieldType, IField } from '../../../../models/field/field.model';
import { BaseGenericInputComponent } from '../base-generic-input/base-generic-input.component';

@Component({
    selector: 'mat-generic-date-time-input',
    templateUrl: './generic-date-time-input.component.html',
    styleUrls: [ '../base-generic-input/base-generic-input.component.scss', './generic-date-time-input.component.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: MatFormFieldControl,
            useExisting: GenericDateTimeInputComponent,
        },
    ],
})
export class GenericDateTimeInputComponent<T = Date> extends BaseGenericInputComponent<string> implements OnInit {

    @Input()
    public dateTimeSeperator: string = ' ';
    @Input()
    public outputFormatDate: string = 'yyyy-MM-dd';
    @Input()
    public outputFormatTime: string = 'HH:mm:ss';

    public dateField: IField;
    public timeField: IField;

    public formGroup: FormGroup;

    constructor(
        @Optional()
        @Self()
        protected myNgControl: NgControl,
        protected myGenericDateAdapter: DateAdapter<T>,
        protected myChangeDetectorRef: ChangeDetectorRef,
        protected myElementRef: ElementRef,
    ) {
        super(myChangeDetectorRef);

        if (this.myNgControl) {
            this.myNgControl.valueAccessor = this;
        }
    }

    public ngOnInit(): void {
        super.ngOnInit();

        this.formGroup = new FormGroup({
            [ `${this.field.field}_date` ]: new FormControl<string>(this.initialValue?.split(this.dateTimeSeperator)?.[ 0 ]),
            [ `${this.field.field}_time` ]: new FormControl<string>(this.initialValue?.split(this.dateTimeSeperator)?.[ 1 ]),
        });

        this.formGroup.statusChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((status: FormControlStatus) => {
            if (status !== 'VALID') {
                this.inputFormControl.setErrors({ status });
            } else {
                this.inputFormControl.setErrors(null);
            }
        });
        this.inputFormControl.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
            const valid: boolean = this.formGroup.getRawValue()?.[ `${this.field.field}_date` ] && this.formGroup.getRawValue()?.[ `${this.field.field}_time` ]?.length >= 5;
            if (!!value || valid || document.activeElement?.closest('mat-generic-date-time-input') !== this.myElementRef.nativeElement) {
                if (value?.split(this.dateTimeSeperator)?.[ 0 ] !== this.formGroup.getRawValue()?.[ `${this.field.field}_date` ]) {
                    this.formGroup.controls[ `${this.field.field}_date` ].patchValue(value?.split(this.dateTimeSeperator)?.[ 0 ]);
                }
                if (value?.split(this.dateTimeSeperator)?.[ 1 ]?.slice(0,5) !== this.formGroup.getRawValue()?.[ `${this.field.field}_time` ]) {
                    this.formGroup.controls[ `${this.field.field}_time` ].patchValue(value?.split(this.dateTimeSeperator)?.[ 1 ]?.slice(0,5));
                }

            }

        });
        this.formGroup.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((formData: Record<string, T | string>) => {
            // const dateString: string = formData[ `${this.field.field}_date` ] as string;
            const dateString: string = formData[ `${this.field.field}_date` ] ? this.myGenericDateAdapter.toIso8601(formData[ `${this.field.field}_date` ] as T) : null;
            const timeString: string = formData[ `${this.field.field}_time` ] as string;
            if (dateString && timeString?.length === 5) {
                const res: string = formatDate(`${dateString}T${timeString}`, this.outputFormatDate + this.dateTimeSeperator + this.outputFormatTime, this.myGenericDateAdapter?.[ 'locale' ] || 'en');
                if (res !== this.inputFormControl.value) {
                    this.inputFormControl.patchValue(formatDate(`${dateString}T${timeString}`, this.outputFormatDate + this.dateTimeSeperator + this.outputFormatTime, this.myGenericDateAdapter?.[ 'locale' ] || 'en'));
                }
            } else {
                this.inputFormControl.patchValue(null);
            }
            // const displayFormat: string = (this.temporalDateAdapterOptions.outputFormat || this.momentDateAdapterOptions[ 'outputFormat' ] || 'yyyy-MM-dd').split(' ')[ 0 ];
            // this.inputFormControl
            //     .patchValue(this.myDateAdapter.parse(`${dateString} ${timeString}`, `${displayFormat} HH:mm:ss`));
        });

        this.dateField = {
            field: `${this.field.field}_date`,
            label: this.field.label,
            type: FieldType.date,
            required: this.field?.required,
        };

        this.timeField = {
            field: `${this.field.field}_time`,
            label: 'sla-ui-material.generic-inputs[dateTime][time].label',
            type: FieldType.mask,
            mask: '´´:´´',
            maskSlotChar: ':',
            maskDefs: { '´': /[0-9]/ },
            required: this.field?.required,
            validatorFn: [ this.validateTime(), Validators.minLength(5) ],
        };
    }
    public focusDate(): void {
        document.getElementById(`${this.field.field}_date`)?.focus();
        document.getElementById(`${this.field.field}_date`)?.[ 'select' ]?.();
    }
    public focusTime(): void {
        document.getElementById(`${this.field.field}_time`)?.focus();
        document.getElementById(`${this.field.field}_time`)?.[ 'select' ]?.();
    }

    protected validateTime(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors => {
            if (control.value) {
                const splitted: string[] = control.value.toString().split(':', 2);
                if (+splitted[ 0 ] >= 24) {
                    splitted[ 0 ] = '23';
                    control.setValue(`${splitted[ 0 ]}:${splitted[ 1 ]}`);
                }
                if (+splitted[ 1 ] >= 60) {
                    splitted[ 1 ] = '59';
                    control.setValue(`${splitted[ 0 ]}:${splitted[ 1 ]}`);
                }
            }
            return null;
        };
    }

}
